import { Injectable } from '@angular/core'
import { ModalPositionService } from '@helios/user-preferences'
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap'
import { BulkCashEntryComponent } from '../../components/bulk-transaction/bulk-cash-entry/bulk-cash-entry.component'
import { BulkDirectTransferModalComponent } from '../../components/bulk-transaction/bulk-direct-transfer/bulk-direct-transfer.component'
import { BulkFxForwardComponent } from '../../components/bulk-transaction/bulk-fx-forward/bulk-fx-forward.component'
import { BulkFxSpotTradeComponent } from '../../components/bulk-transaction/bulk-fx-spot-trade/bulk-fx-spot-trade.component'
import { BulkLoadComponent } from '../../components/bulk-transaction/bulk-load/bulk-load.component'
import { BulkSecurityExchangeComponent } from '../../components/bulk-transaction/bulk-security-exchange/bulk-security-exchange.component'
import { BulkSecurityTransferInComponent } from '../../components/bulk-transaction/bulk-security-transfer-in/bulk-security-transfer-in.component'
import { BulkSecurityTransferOutComponent } from '../../components/bulk-transaction/bulk-security-transfer-out/bulk-security-transfer-out.component'
import { BulkTradeEntryModalComponent } from '../../components/bulk-transaction/bulk-trade-entry/bulk-trade-entry.component'
import { BulkTransactionModalResponse } from '../../components/bulk-transaction/bulk-transaction-modal-response'
import { BulkWireTransferComponent } from '../../components/bulk-transaction/bulk-wire-transfer/bulk-wire-transfer.component'
import { BulkTransactionModalInput } from '../../model/bulk-transaction/bulk-popup-input-model'
import { TranCategory } from '../../model/transaction-category.model'
import { TransactionTypeKey } from '../../model/transaction-type.model'
import { getTransactionCategoryFromTypeKey } from '../../util/common-utils'

/** Service for showing and hiding the advanced filters modal. */
@Injectable({
  providedIn: 'root',
})
export class BulkTransactionsModalService {
  constructor(
    private readonly modalService: NgbModal,
    readonly modalPositionService: ModalPositionService,
  ) {}

  /**
   * required to mediate calls to respective functions based on transTypeCode
   *
   * @param transTypekey transaction type input
   * @param transInput transaction input
   */
  toggleBulkTransactionModal(
    transTypekey: TransactionTypeKey,
    transInput: BulkTransactionModalInput,
  ): Promise<BulkTransactionModalResponse> {
    const tranCategory = getTransactionCategoryFromTypeKey(transTypekey)
    switch (tranCategory) {
      case TranCategory.CASH_ENTRY:
        return this.toggleCashEntryModal(transInput)
      case TranCategory.TRADE:
        return this.toggleTradeEntryModal(transInput)
      case TranCategory.CURRENCY_FORWARD:
        return this.toggleFxForwardModal(transInput)
      case TranCategory.FX_TRADE:
        return this.toggleFxSpotTradeModal(transInput)
      case TranCategory.DIRECT_TRANSFER:
        return this.toggleWireTransferModal(transInput)
      case TranCategory.TRANSFER:
        if (transInput.isTwoSidedTrno) {
          return this.toggleDirectTransfers(transInput)
        }
        return this.toggleTransfers(transTypekey, transInput)
      default:
        return Promise.reject()
    }
  }

  toggleTransfers(
    transTypekey: TransactionTypeKey,
    transInput: BulkTransactionModalInput,
  ) {
    if ('INITIAL_LOAD'.includes(transTypekey)) {
      return this.toggleInitialLoadModal(transInput)
    } else if (['TRANSFER_IN', 'EXCHANGE_IN'].includes(transTypekey)) {
      return this.toggleSecurityTransferInModal(transInput)
    } else {
      return this.toggleSecurityTransferOutModal(transInput)
    }
  }

  toggleDirectTransfers(transInput: BulkTransactionModalInput) {
    return this.toggleDirectTransferOutModal(transInput)
  }

  /**
   * cash type modal opener
   *
   * @param transInput transaction input
   */
  toggleCashEntryModal(
    transInput: BulkTransactionModalInput,
  ): Promise<BulkTransactionModalResponse> {
    return this.toggleModal(
      transInput,
      BulkCashEntryComponent,
      'bulk-cash-entry',
    )
  }

  /**
   * cash type modal opener
   *
   * @param transInput transaction input
   */
  toggleDirectTransferModal(
    transInput: BulkTransactionModalInput,
  ): Promise<BulkTransactionModalResponse> {
    return this.toggleModal(
      transInput,
      BulkDirectTransferModalComponent,
      'bulk-direct-transfer',
      'xxl',
    )
  }

  toggleTradeEntryModal(
    transInput: BulkTransactionModalInput,
  ): Promise<BulkTransactionModalResponse> {
    return this.toggleModal(
      transInput,
      BulkTradeEntryModalComponent,
      'bulk-trade-entry',
      'xxl',
    )
  }

  /**
   * cash type modal opener
   *
   * @param transInput transaction input
   */
  toggleFxForwardModal(
    transInput: BulkTransactionModalInput,
  ): Promise<BulkTransactionModalResponse> {
    return this.toggleModal(
      transInput,
      BulkFxForwardComponent,
      'bulk-fx-forward',
    )
  }

  toggleFxSpotTradeModal(
    transInput: BulkTransactionModalInput,
  ): Promise<BulkTransactionModalResponse> {
    return this.toggleModal(
      transInput,
      BulkFxSpotTradeComponent,
      'bulk-fx-spot-trade',
    )
  }

  toggleWireTransferModal(
    transInput: BulkTransactionModalInput,
  ): Promise<BulkTransactionModalResponse> {
    return this.toggleModal(
      transInput,
      BulkWireTransferComponent,
      'bulk-wire-transfer',
    )
  }

  toggleInitialLoadModal(
    transInput: BulkTransactionModalInput,
  ): Promise<BulkTransactionModalResponse> {
    return this.toggleModal(transInput, BulkLoadComponent, 'bulk-load', 'xxl')
  }

  toggleSecurityTransferInModal(
    transInput: BulkTransactionModalInput,
  ): Promise<BulkTransactionModalResponse> {
    return this.toggleModal(
      transInput,
      BulkSecurityTransferInComponent,
      'bulk-security-transfer-in',
      'xxl',
    )
  }

  toggleSecurityTransferOutModal(
    transInput: BulkTransactionModalInput,
  ): Promise<BulkTransactionModalResponse> {
    return this.toggleModal(
      transInput,
      BulkSecurityTransferOutComponent,
      'bulk-security-transfer-out',
      'xl',
    )
  }

  toggleDirectTransferOutModal(
    transInput: BulkTransactionModalInput,
  ): Promise<BulkTransactionModalResponse> {
    return this.toggleModal(
      transInput,
      BulkDirectTransferModalComponent,
      'bulk-direct-transfer',
      'xxl',
    )
  }

  toggleSecurityExchangeModal(
    transInput: BulkTransactionModalInput,
  ): Promise<BulkTransactionModalResponse> {
    return this.toggleModal(
      transInput,
      BulkSecurityExchangeComponent,
      'bulk-security-exchange',
      'xxl',
    )
  }

  private toggleModal(
    transInput: BulkTransactionModalInput,
    modalComponent: unknown,
    key: string,
    size = 'xl',
  ): Promise<BulkTransactionModalResponse> {
    const uniqueId = key + '-' + Date.now()
    const modalRef = this.modalService.open(modalComponent, {
      size,
      windowClass: `modal-window-click-through ${uniqueId}`,
      keyboard: false,
      beforeDismiss: () => {
        return this.modalPositionService.handleModalPositionSaveOnDismiss(
          uniqueId,
          key,
        )
      },
    } as NgbModalOptions)

    modalRef.componentInstance.transInput = transInput
    this.modalPositionService.handleModalPositionSetup(uniqueId, key)
    return modalRef.result.catch(() => {
      //User closed modal
      return {
        success: true,
        transactionRequests: [],
      } as BulkTransactionModalResponse
    })
  }
}
